import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Plus } from "lucide-react";
import { useCatalogsStore } from "../../../store/Catalogs";

interface CatalogDialogProps {
	asCard?: boolean;
}

export const CatalogDialog: React.FC<CatalogDialogProps> = ({ asCard = false }) => {
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const addCustomCatalog = useCatalogsStore((state) => state.addCustomCatalog);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!name.trim()) {
			return;
		}

		setIsSubmitting(true);
		try {
			await addCustomCatalog(name.trim());
			setOpen(false);
			setName("");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
		if (!newOpen) {
			setName("");
		}
	};

	const triggerElement = asCard ? (
		<div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:bg-gray-50 hover:border-gray-400 transition-colors cursor-pointer group flex flex-col items-center justify-center h-full min-h-[120px]">
			<Plus className="w-8 h-8 text-gray-400 group-hover:text-gray-600 mb-2" />
			<span className="text-sm font-medium text-gray-600 group-hover:text-gray-800">
				Neuer Katalog
			</span>
		</div>
	) : (
		<Button size="sm" variant="outline" className="h-7 px-2 flex items-center gap-1">
			<Plus className="h-3 w-3" />
			<span className="text-xs">Neuer Katalog</span>
		</Button>
	);

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogTrigger asChild>
				{triggerElement}
			</DialogTrigger>
			<DialogContent className="sm:max-w-[425px]">
				<form onSubmit={handleSubmit}>
					<DialogHeader>
						<DialogTitle>Neuen Katalog erstellen</DialogTitle>
						<DialogDescription>
							Erstellen Sie einen benutzerdefinierten Katalog für Ihre Organisation.
						</DialogDescription>
					</DialogHeader>
					<div className="grid gap-4 py-4">
						<div className="grid grid-cols-4 items-center gap-4">
							<Label htmlFor="name" className="text-right">
								Name
							</Label>
							<Input
								id="name"
								value={name}
								onChange={(e) => setName(e.target.value)}
								className="col-span-3"
								placeholder="z.B. Spezialtarife 2024"
								required
								autoFocus
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={isSubmitting}
						>
							Abbrechen
						</Button>
						<Button type="submit" disabled={isSubmitting || !name.trim()}>
							{isSubmitting ? "Erstelle..." : "Erstellen"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};